import React, { useState } from 'react';
import { Search, Calendar, User, ArrowRight, Clock } from 'lucide-react';

const BlogPage = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const blogPosts = [
    {
      id: 1,
      title: "Understanding ADHD: A Complete Guide for Beginners",
      excerpt: "ADHD is more than just difficulty focusing. Learn about the neuroscience behind attention deficit hyperactivity disorder and how it affects daily life.",
      category: "Education",
      date: "March 28, 2026",
      readTime: "8 min read",
      author: "Victor Grösser"
    },
    {
      id: 2,
      title: "10 Productivity Hacks That Actually Work for ADHD Brains",
      excerpt: "Traditional productivity advice doesn't work for ADHD. Discover science-backed strategies that leverage your brain's unique wiring.",
      category: "Productivity",
      date: "March 26, 2026",
      readTime: "6 min read",
      author: "Victor Grösser"
    },
    {
      id: 3,
      title: "The ADHD-Time Blindness Connection: How to Manage Time Better",
      excerpt: "Time blindness is a core ADHD symptom. Learn practical techniques to overcome it and never be late again.",
      category: "Time Management",
      date: "March 24, 2026",
      readTime: "7 min read",
      author: "Victor Grösser"
    },
    {
      id: 4,
      title: "Medication vs. Natural Approaches: Finding What Works for You",
      excerpt: "The ADHD treatment debate isn't one-size-fits-all. Explore both medication and natural approaches to find your optimal path.",
      category: "Treatment",
      date: "March 22, 2026",
      readTime: "10 min read",
      author: "Victor Grösser"
    },
    {
      id: 5,
      title: "ADHD and Relationships: Communication Strategies That Work",
      excerpt: "ADHD can strain relationships, but with the right communication tools, you can build stronger connections.",
      category: "Relationships",
      date: "March 20, 2026",
      readTime: "9 min read",
      author: "Victor Grösser"
    },
    {
      id: 6,
      title: "The Hyperfocus Superpower: Channeling Your ADHD Intensity",
      excerpt: "Hyperfocus isn't just a symptom—it's a superpower when harnessed correctly. Learn how to use it to your advantage.",
      category: "Strengths",
      date: "March 18, 2026",
      readTime: "5 min read",
      author: "Victor Grösser"
    },
    {
      id: 7,
      title: "ADHD-Friendly Meal Planning: Eating Well Without the Stress",
      excerpt: "Meal planning with ADHD doesn't have to be complicated. Simple strategies for nutritious eating that fit your lifestyle.",
      category: "Health",
      date: "March 16, 2026",
      readTime: "6 min read",
      author: "Victor Grösser"
    },
    {
      id: 8,
      title: "Financial Management with ADHD: Budgeting Without Burnout",
      excerpt: "Impulse spending and ADHD often go hand in hand. Discover systems that make money management automatic and stress-free.",
      category: "Finance",
      date: "March 14, 2026",
      readTime: "8 min read",
      author: "Victor Grösser"
    },
    {
      id: 9,
      title: "The ADHD Sleep Connection: Why You Can't Shut Off Your Brain",
      excerpt: "Sleep problems affect 70% of adults with ADHD. Learn science-backed strategies to improve your sleep hygiene.",
      category: "Health",
      date: "March 12, 2026",
      readTime: "7 min read",
      author: "Victor Grösser"
    },
    {
      id: 10,
      title: "Building Routines That Stick: The ADHD-Friendly Approach",
      excerpt: "Traditional routine advice fails ADHD brains. Here's how to build habits that actually last.",
      category: "Habits",
      date: "March 10, 2026",
      readTime: "6 min read",
      author: "Victor Grösser"
    },
  ];

  const categories = ["All", "Education", "Productivity", "Time Management", "Treatment", "Relationships", "Strengths", "Health", "Finance", "Habits"];

  const filteredPosts = blogPosts.filter(post => 
    post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      <section className="bg-gradient-to-r from-teal-600 to-emerald-600 text-white py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-5xl font-bold mb-4">ADHD Life Blog</h1>
          <p className="text-xl opacity-90">Practical advice and insights for living well with ADHD</p>
        </div>
      </section>

      <section className="py-12">
        <div className="container mx-auto px-4">
          {/* Search and Filters */}
          <div className="max-w-6xl mx-auto mb-12">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setSearchTerm(cat === "All" ? "" : cat)}
                    className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
                      (cat === "All" && searchTerm === "") || searchTerm === cat
                        ? 'bg-teal-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Blog Posts Grid */}
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <article key={post.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition">
                  <div className="h-48 bg-gradient-to-br from-teal-100 to-emerald-100 flex items-center justify-center">
                    <div className="text-6xl opacity-20">📝</div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-teal-600 font-semibold">{post.category}</span>
                      <span className="text-sm text-gray-500 flex items-center">
                        <Clock size={14} className="mr-1" /> {post.readTime}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold mb-3 text-gray-800">{post.title}</h3>
                    <p className="text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-gray-500">
                        <User size={14} className="mr-1" /> {post.author}
                      </div>
                      <button className="text-teal-600 font-semibold hover:text-teal-800 flex items-center">
                        Read <ArrowRight size={16} className="ml-1" />
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {filteredPosts.length === 0 && (
              <div className="text-center py-12">
                <p className="text-xl text-gray-600">No articles found matching your search.</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;